import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { Row, Col, Button } from 'antd';
import { useMount, useUpdateEffect, useDebounce } from 'ahooks';
import { ButtonType } from 'antd/lib/button';
import { TablePaginationConfig } from 'antd/es/table';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ActionsButton, { ActionOption } from './ActionButton';
import Search from './SearchInput';
import TablePaginator from './TablePaginator';

const PanelColumn = styled(Col)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ButtonOptionsContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-left: 25px;
`;

const columnSpacing = (condition: boolean, result1: string, result2: string) =>
  condition ? result1 : result2;

export interface Props {
  pagination?: TablePaginationConfig;
  total?: number;
  searchValue?: string;
  handleSearchChange: (v: any) => void;
  handleActionTriggered?: (a: string) => void;
  handlePaginationChange?: (current: number, pageSize: number) => void;
  actionOptionsLoading?: boolean;
  actionOptions?: ActionOption[];
  helpCenterLink?: string;
  createOptions?: ActionOption[];
  actionOptionsSelectedItems?: number[];
  actionButtonLabel?: string;
  createButtonLabel?: string;
  actionButtonType?: ButtonType;
  createButtonType?: ButtonType;
  actionButtonIcon?: ReactElement;
  handleActionButtonClick?: (v: any) => void;
  handleCreateButtonClick?: (v: any) => void;
  saveButtonLabel?: string;
  saveButtonType?: ButtonType;
  saveButtonIcon?: ReactElement;
  saveButtonDisabled?: boolean;
  handleSaveButtonClick?: (v: any) => void;
  actionButtonLoading?: boolean;
}

interface HelpCenterLinkComponentProps {
  url: string;
  style?: React.CSSProperties;
}
export const HelpCenterLinkComponent: React.FC<
  React.PropsWithChildren<HelpCenterLinkComponentProps>
> = ({ url, style }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" style={style}>
    <QuestionCircleOutlined />
  </a>
);

const TablePanel: React.FC<React.PropsWithChildren<Props>> = ({
  handleSearchChange,
  handlePaginationChange = () => {},
  handleActionTriggered,
  handleCreateButtonClick,
  actionOptionsLoading = false,
  helpCenterLink,
  actionOptions = [],
  createOptions = [],
  createButtonLabel,
  actionOptionsSelectedItems = [],
  actionButtonLabel,
  actionButtonType = 'primary',
  createButtonType = 'default',
  actionButtonIcon,
  handleActionButtonClick,
  saveButtonLabel,
  saveButtonType = 'primary',
  saveButtonIcon,
  handleSaveButtonClick,
  saveButtonDisabled = false,
  pagination,
  total,
  children = null,
  searchValue: storedSearchValue,
}) => {
  const [searchValue, setSearchValue] = React.useState<string | undefined>();
  const debouncedValue = useDebounce(searchValue, { wait: 500 });

  useMount(() => {
    if (storedSearchValue) {
      setSearchValue(storedSearchValue);
    }
  });

  useUpdateEffect(() => {
    handleSearchChange(debouncedValue);
  }, [debouncedValue]);

  return (
    <Row css="padding: 20px 0">
      <PanelColumn span={12}>
        <Search
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </PanelColumn>
      {actionButtonLabel !== undefined ||
      actionOptions.length > 0 ||
      createOptions.length > 0 ? (
        <PanelColumn span={columnSpacing(createOptions.length > 0, '6', '4')}>
          <ButtonOptionsContainer>
            {actionButtonLabel !== undefined && (
              <Button
                onClick={handleActionButtonClick}
                type={actionButtonType}
                icon={actionButtonIcon}
              >
                {actionButtonLabel}
              </Button>
            )}
            {actionOptions.length > 0 && (
              <ActionsButton
                css="margin-left: 10px"
                actionOptions={actionOptions}
                selectedItems={actionOptionsSelectedItems}
                loading={actionOptionsLoading}
                handleClick={(action) => {
                  if (handleActionTriggered) {
                    handleActionTriggered(action);
                  }
                }}
              />
            )}
            {createOptions.length > 0 && (
              <ActionsButton
                actionType="outbound"
                createButtonLabel={createButtonLabel}
                createButtonType={createButtonType}
                actionOptions={createOptions}
                selectedItems={actionOptionsSelectedItems}
                loading={actionOptionsLoading}
                handleClick={(action) => {
                  if (handleCreateButtonClick) {
                    handleCreateButtonClick(action);
                  }
                }}
              />
            )}
            {helpCenterLink !== undefined && (
              <HelpCenterLinkComponent
                url={helpCenterLink}
                style={{
                  marginLeft: 5,
                  marginTop: 5,
                }}
              />
            )}
          </ButtonOptionsContainer>
        </PanelColumn>
      ) : null}

      {pagination ? (
        <PanelColumn css="margin-left:auto">
          <TablePaginator
            style={{
              float: 'left',
            }}
            onChange={handlePaginationChange}
            total={pagination.total ?? total}
            pagination={pagination}
          />
          {children}
        </PanelColumn>
      ) : null}
      {saveButtonLabel !== undefined && !pagination ? (
        <PanelColumn css="margin-left:auto">
          <Button
            disabled={saveButtonDisabled}
            onClick={handleSaveButtonClick}
            type={saveButtonType}
            icon={saveButtonIcon}
          >
            {saveButtonLabel}
          </Button>
        </PanelColumn>
      ) : null}
    </Row>
  );
};

export default TablePanel;
